import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [spotsRemaining] = useState(3); // Track remaining spots
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Handle scroll for navbar - add cleanup for event listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobileMenuOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://formspree.io/f/xqeeajkd', {
        method: 'POST',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          message: formData.message,
          _subject: 'New Contact Form Submission - Cader Talent',
          _replyto: formData.email
        }),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        // Redirect to thank you page instead of showing success message
        window.location.href = '/thank-you';
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMobileMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-0 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://static.readdy.ai/image/5287d4f4544f3b52e73e5c6adbaab8fc/8a8de03178bcb555df0aaf16906b9e92.png" alt="Cader Talent" className="h-40 w-auto" />
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <a href="#how-it-works" className={`text-base font-medium transition-colors hover:text-[#C67B5C] ${isScrolled ? 'text-[#1A1A1A]' : 'text-white'}`}>How It Works</a>
            <a href="#why-us" className={`text-base font-medium transition-colors hover:text-[#C67B5C] ${isScrolled ? 'text-[#1A1A1A]' : 'text-white'}`}>Why Us</a>
            <a href="#pricing" className={`text-base font-medium transition-colors hover:text-[#C67B5C] ${isScrolled ? 'text-[#1A1A1A]' : 'text-white'}`}>Pricing</a>
            <Link to="/cost-calculator" className={`text-base font-medium transition-colors hover:text-[#C67B5C] ${isScrolled ? 'text-[#1A1A1A]' : 'text-white'}`}>Cost Calculator</Link>
            <a href="#about" className={`text-base font-medium transition-colors hover:text-[#C67B5C] ${isScrolled ? 'text-[#1A1A1A]' : 'text-white'}`}>About</a>
            <a href="tel:+18329243888" className={`flex items-center gap-2 text-base font-medium transition-colors hover:text-[#C67B5C] ${isScrolled ? 'text-[#1A1A1A]' : 'text-white'}`}>
              <i className="ri-phone-line"></i>
              (832) 924-3888
            </a>
            <a href="#contact" className="bg-[#C67B5C] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#B36A4D] transition-colors whitespace-nowrap cursor-pointer">Book a Conversation</a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${isScrolled ? 'text-[#1A1A1A] hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
            aria-label="Toggle menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden bg-white shadow-lg border-t border-gray-100"
            onClick={handleMobileMenuClick}
          >
            <div className="px-6 py-4 space-y-4">
              <a href="#how-it-works" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">How It Works</a>
              <a href="#why-us" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">Why Us</a>
              <a href="#pricing" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">Pricing</a>
              <Link to="/cost-calculator" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">Cost Calculator</Link>
              <a href="#about" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">About</a>
              <a href="tel:+18329243888" onClick={closeMobileMenu} className="flex items-center gap-2 text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">
                <i className="ri-phone-line"></i>
                (832) 924-3888
              </a>
              <a href="#contact" onClick={closeMobileMenu} className="block bg-[#C67B5C] text-white px-6 py-3 rounded-full font-medium hover:bg-[#B36A4D] transition-colors text-center whitespace-nowrap cursor-pointer">Book a Conversation</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-32">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/324c5f3e7f9fa95cd6ff013dd97be8cd.jpg" 
            alt="San Salvador, El Salvador at night" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 text-white">
              {/* Value Proposition Badge */}
              <div className="inline-flex items-center gap-3 bg-[#C67B5C] text-white px-5 py-2.5 rounded-full text-sm font-bold mb-4 shadow-lg">
                <i className="ri-shield-check-line text-lg"></i>
                <span>NO DEPOSIT – Pay only if you hire</span>
              </div>
              
              <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-wider mb-6">Founder-Led Talent Partnership</p>
              <h1 className="text-6xl font-bold leading-tight mb-6">
                <span className="block">LATAM Is Your</span>
                <span className="block">Best Hire.</span>
              </h1>
              <p className="text-2xl text-white/90 mb-12 leading-relaxed max-w-2xl">
                World-class talent. Same time zones. Unbeatable value. El Salvador offers exceptional professionals who combine US-level skills with cultural alignment—at a fraction of the cost.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <a href="#contact" className="inline-flex items-center gap-3 bg-[#C67B5C] text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-[#B36A4D] transition-all hover:gap-4 cursor-pointer whitespace-nowrap">
                  Book a Conversation
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
                <a href="tel:+18329243888" className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-white/20 transition-all border-2 border-white/30 cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line text-xl"></i>
                  Call Us Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NO DEPOSIT Value Proposition Section */}
      <section className="py-16 bg-gradient-to-r from-[#C67B5C] to-[#B36A4D] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left flex-1">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                <i className="ri-shield-check-line text-xl text-white"></i>
                <span className="text-white text-sm font-bold uppercase tracking-wider">Our Commitment</span>
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold text-white mb-4">
                NO DEPOSIT
              </h2>
              <p className="text-xl text-white/90 mb-6 leading-relaxed">
                We're so confident in our candidates that we don't ask for payment upfront. You only pay when you hire.
              </p>
              
              {/* How It Works */}
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-5 mb-4 text-left">
                <h3 className="text-white font-bold text-lg mb-3">How it works:</h3>
                <ul className="space-y-2 text-white/95">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl flex-shrink-0 mt-0.5"></i>
                    <span>We vet and present 2–3 qualified candidates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl flex-shrink-0 mt-0.5"></i>
                    <span>You interview and choose who fits best</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Pay $1,000 only when you make the hire</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-5 py-3 inline-block">
                <p className="text-white text-base">
                  <strong className="font-bold">Zero risk. Zero upfront cost.</strong><br />
                  This is how confident we are in our talent.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a 
                href="tel:+18329243888" 
                className="inline-flex items-center gap-3 bg-white text-[#C67B5C] px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/90 transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                Call (832) 924-3888
              </a>
              <a 
                href="mailto:mauricio@cadertalent.com" 
                className="inline-flex items-center gap-3 bg-transparent text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/10 transition-all border-2 border-white cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Modern Hiring Needs a New Approach */}
      <section className="py-32 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-8 mb-6">
            <div className="h-px w-32 bg-[#C67B5C]/20"></div>
            <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-widest">The Problem</p>
            <div className="h-px w-32 bg-[#C67B5C]/20"></div>
          </div>
          <h2 className="text-5xl font-serif italic text-[#1A1A1A] mb-8">AI made applications meaningless</h2>
          <p className="text-lg text-[#1A1A1A]/80 leading-relaxed mb-8">
            Every resume is now polished by ChatGPT. Every cover letter sounds the same. The written application has become noise, not signal. Founders waste hours reading perfect-sounding applications from candidates who can't do the job.
          </p>
          <p className="text-lg text-[#1A1A1A]/80 leading-relaxed mb-12">
            The solution isn't more AI screening. It's getting back to what actually works: short applications, live conversations, and human judgment.
          </p>
          <blockquote className="text-4xl font-serif italic text-[#C67B5C] leading-snug">
            "Signal comes from conversation, not documentation"
          </blockquote>
        </div>
      </section>

      {/* Cost Calculator CTA */}
      <section className="py-24 bg-[#F5F5F3]">
        <div className="max-w-4xl mx-auto px-8">
          <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#C67B5C]/10 mb-6">
              <i className="ri-calculator-line text-4xl text-[#C67B5C]"></i>
            </div>
            <h2 className="text-4xl font-bold text-[#1A1A1A] mb-4">See how much you could save</h2>
            <p className="text-lg text-[#1A1A1A]/70 mb-8 max-w-2xl mx-auto">
              Calculate your potential savings by hiring vetted talent from El Salvador. Most companies save 50–70% on hiring costs.
            </p>
            <Link 
              to="/cost-calculator" 
              className="inline-flex items-center gap-3 bg-[#C67B5C] text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-[#B36A4D] transition-all hover:gap-4 cursor-pointer whitespace-nowrap"
            >
              Calculate Your Savings
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-[#F5F5F3]">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-widest mb-4">The Process</p>
            <h2 className="text-5xl font-bold text-[#1A1A1A]">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-16 left-0 right-0 h-0.5 border-t-2 border-dotted border-[#C67B5C]/40" style={{ top: '40px' }}></div>
            
            {[
              { num: '01', title: 'Schedule a Call', desc: 'Review role requirements, agree on a budget, and approve the job description.' },
              { num: '02', title: 'We Search & Screen', desc: 'We post and advertise your job, field applications, and screen candidates.' },
              { num: '03', title: 'Candidates Presented', desc: 'We present 2–3 vetted candidates to interview, typically within 2–3 weeks.' },
              { num: '04', title: 'Make Your Hire', desc: 'If it doesn\'t work out within 90 days, we search for a replacement.' }
            ].map((step, index) => (
              <div key={index} className="relative bg-white p-6 rounded-2xl">
                <div className="text-8xl font-serif text-[#C67B5C]/30 mb-4 leading-none">{step.num}</div>
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3">{step.title}</h3>
                <p className="text-base text-[#1A1A1A]/70 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Hiring Process */}
      <section className="py-24 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-widest mb-4">Our Method</p>
            <h2 className="text-5xl font-bold text-[#1A1A1A]">Our Hiring Process</h2>
          </div>
          <div className="space-y-6">
            {[
              { num: '1', title: 'Short application' },
              { num: '2', title: '10-minute live interview with all potential candidates' },
              { num: '3', title: 'Internal scorecard' },
              { num: '4', title: 'Client interview (2–3 candidates only)' },
              { num: '5', title: 'If it doesn\'t work out within 90 days, we search for a replacement' }
            ].map((step, index) => (
              <div key={index} className="flex items-start gap-6 bg-white p-6 rounded-2xl hover:shadow-md transition-shadow">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C67B5C] flex items-center justify-center">
                  <span className="text-white text-xl font-bold">{step.num}</span>
                </div>
                <div className="flex-1 pt-2">
                  <p className="text-lg font-medium text-[#1A1A1A]">{step.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Short Application & 10-Minute Interview */}
      <section className="py-24 bg-[#F5F5F3]">
        <div className="max-w-3xl mx-auto px-8">
          <h2 className="text-4xl font-bold text-[#1A1A1A] mb-12 text-center">Why a short application and a 10-minute interview?</h2>
          
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-2xl">
              <p className="text-lg text-[#1A1A1A]/80 leading-relaxed mb-4">
                Today, AI has made written applications almost meaningless.
              </p>
              <p className="text-lg font-medium text-[#1A1A1A]">
                Everyone can sound perfect on paper.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl">
              <p className="text-lg text-[#1A1A1A]/80 leading-relaxed">
                Instead of over-optimizing forms and tests, we focus on real-time conversation.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl">
              <p className="text-lg text-[#1A1A1A]/80 leading-relaxed mb-4">
                A short application filters intent.
              </p>
              <p className="text-lg font-medium text-[#1A1A1A]">
                A 10-minute live interview reveals communication, judgment, accountability, and cultural fit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Cader Talent */}
      <section id="why-us" className="py-32 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-widest mb-4">Why Us</p>
              <h2 className="text-5xl font-bold text-[#1A1A1A] mb-6 leading-tight">Accountability on every hire</h2>
              <p className="text-lg text-[#1A1A1A]/70 leading-relaxed">
                This isn't a job board or a recruiting agency. It's a founder helping other founders hire well. Every candidate is personally vetted. Every placement is backed by follow-up and accountability.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: 'ri-user-search-line', title: 'Personally Vetted', desc: 'Every candidate speaks with Mauricio before being presented to you.' },
                { icon: 'ri-global-line', title: 'Cultural Alignment', desc: 'El Salvador shares time zones and work culture with the US—no 12-hour delays.' },
                { icon: 'ri-timer-line', title: 'Short Hiring Cycles', desc: 'From first conversation to hire in 1–2 weeks, not months.' },
                { icon: 'ri-shield-check-line', title: 'Accountability', desc: 'If it doesn\'t work out within 90 days, we search for a replacement.' }
              ].map((item, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer">
                  <i className={`${item.icon} text-4xl text-[#C67B5C] mb-4 block`}></i>
                  <h3 className="text-lg font-bold text-[#1A1A1A] mb-3">{item.title}</h3>
                  <p className="text-sm text-[#1A1A1A]/70 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials & Case Studies */}
      <section className="py-32 bg-[#F5F5F3]">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-widest mb-4">Success Stories</p>
            <h2 className="text-5xl font-bold text-[#1A1A1A] mb-6">What Our Clients Say</h2>
            <p className="text-lg text-[#1A1A1A]/70 max-w-2xl mx-auto">Real results from founders who trusted us with their hiring needs</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                quote: "Mauricio found us an SDR who outperformed our US-based reps within the first month. The cultural fit was perfect, and the cost savings allowed us to hire two more team members.",
                name: "Sarah Mitchell",
                role: "CEO",
                company: "TechFlow Solutions",
                result: "70% cost reduction",
                image: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20confident%20female%20CEO%20in%20her%2040s%20with%20warm%20smile%20wearing%20business%20casual%20attire%20neutral%20background%20soft%20lighting%20corporate%20portrait%20photography&width=200&height=200&seq=testimonial-sarah-001&orientation=squarish"
              },
              {
                quote: "After months of bad hires from job boards, Cader Talent delivered three excellent candidates in just two weeks. Our customer support scores have never been higher.",
                name: "David Chen",
                role: "Operations Director",
                company: "GrowthBase",
                result: "3 hires in 2 weeks",
                image: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20an%20Asian%20American%20male%20executive%20in%20his%2030s%20wearing%20glasses%20smart%20casual%20attire%20friendly%20expression%20neutral%20background%20soft%20studio%20lighting%20corporate%20portrait&width=200&height=200&seq=testimonial-david-002&orientation=squarish"
              },
              {
                quote: "The 10-minute interview process actually works. Mauricio's vetting saved us countless hours, and our EA has been with us for over a year now. Best hiring decision we made.",
                name: "Jennifer Adams",
                role: "Founder",
                company: "Bloom Marketing",
                result: "1+ year retention",
                image: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20creative%20female%20entrepreneur%20in%20her%2030s%20with%20natural%20makeup%20stylish%20appearance%20warm%20smile%20neutral%20background%20soft%20natural%20lighting%20modern%20portrait%20photography&width=200&height=200&seq=testimonial-jennifer-003&orientation=squarish"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center gap-2 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#C67B5C] text-lg"></i>
                  ))}
                </div>
                <p className="text-base text-[#1A1A1A]/80 leading-relaxed mb-8 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-bold text-[#1A1A1A]">{testimonial.name}</p>
                    <p className="text-sm text-[#1A1A1A]/60">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-[#1A1A1A]/10">
                  <div className="flex items-center gap-2">
                    <i className="ri-check-double-line text-[#C67B5C] text-xl"></i>
                    <span className="text-sm font-semibold text-[#C67B5C]">{testimonial.result}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats Bar */}
          <div className="bg-white rounded-3xl p-8 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: '50+', label: 'Successful Placements' },
                { value: '92%', label: 'First-Year Retention' },
                { value: '2 weeks', label: 'Average Time to Hire' },
                { value: '60%', label: 'Average Cost Savings' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-4xl font-bold text-[#C67B5C] mb-2">{stat.value}</p>
                  <p className="text-sm text-[#1A1A1A]/60">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Roles We Help Hire */}
      <section className="py-24 bg-[#FAFAF8]">
        <div className="max-w-5xl mx-auto px-8">
          <h2 className="text-4xl font-serif text-[#1A1A1A] text-center mb-12">Roles we help hire</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'SDRs & Sales Support', desc: 'Outbound prospecting, lead qualification, CRM management' },
              { title: 'Customer Support', desc: 'Email, chat, and phone support with US-friendly communication' },
              { title: 'Marketing & CRM Assistants', desc: 'Email campaigns, social media, content coordination' },
              { title: 'Operations & Admin', desc: 'Project coordination, data entry, process management' },
              { title: 'Executive Assistants', desc: 'Calendar management, travel booking, inbox organization' }
            ].map((role, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl border-l-4 border-[#C67B5C] hover:shadow-md transition-shadow cursor-pointer">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{role.title}</h3>
                <p className="text-base text-[#1A1A1A]/70 leading-relaxed">{role.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-widest mb-4">Pricing</p>
            <h2 className="text-5xl font-bold text-[#1A1A1A] mb-6">Simple, transparent pricing</h2>
            <p className="text-lg text-[#1A1A1A]/70 max-w-2xl mx-auto">No hidden fees. No long-term contracts. Just straightforward pricing that aligns our success with yours.</p>
          </div>
          
          <div className="max-w-xl mx-auto">
            {/* Value Proposition Banner */}
            <div className="bg-gradient-to-r from-[#C67B5C] to-[#B36A4D] rounded-2xl p-6 mb-6 text-center shadow-lg relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                  <p className="text-white text-sm font-bold uppercase tracking-wider">Our Commitment</p>
                </div>
                <p className="text-white text-3xl font-bold mb-2">NO DEPOSIT</p>
                <p className="text-white/90 text-base mb-4">Pay only when you hire. Zero risk.</p>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4">
                  <a 
                    href="tel:+18329243888" 
                    className="inline-flex items-center gap-2 bg-white text-[#C67B5C] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    Call (832) 924-3888
                  </a>
                  <a 
                    href="mailto:mauricio@cadertalent.com" 
                    className="inline-flex items-center gap-2 bg-white/20 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-white/30 transition-colors border border-white/30 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-line"></i>
                    Email Us
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg border-2 border-[#C67B5C]">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#C67B5C]/10 mb-6">
                  <i className="ri-user-star-line text-3xl text-[#C67B5C]"></i>
                </div>
                <h3 className="text-3xl font-bold text-[#1A1A1A] mb-2">Individual Plan</h3>
                <div className="mt-6">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-6xl font-bold text-[#C67B5C]">$1,000</span>
                  </div>
                  <p className="text-lg text-[#1A1A1A]/70">Flat fee per placement</p>
                </div>
              </div>

              <div className="bg-[#C67B5C]/5 rounded-2xl p-6 mb-8">
                <p className="text-base text-[#1A1A1A]/80 text-center">
                  <strong className="text-[#C67B5C]">No upfront deposit required.</strong><br />
                  Full fee only due upon completion of placement.
                </p>
              </div>

              <ul className="space-y-4">
                {[
                  '90-Day Free Replacement Guarantee for Hiring',
                  'Save up to 70% compared to US talent',
                  'Vetted & Pre-qualified Candidates',
                  'Full candidate vetting process',
                  'Live interview with Mauricio',
                  '2–3 top candidate matches',
                  'Ongoing placement support'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C67B5C] text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-base text-[#1A1A1A]/80">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <a href="#contact" className="block w-full bg-[#C67B5C] text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-[#B36A4D] transition-colors text-center whitespace-nowrap cursor-pointer">
                  Get Started Today
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#FAFAF8]">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <img 
                src="https://static.readdy.ai/image/5287d4f4544f3b52e73e5c6adbaab8fc/0c0dd4c2ee1358c694b2db6205436944.png" 
                alt="Mauricio Cader, Founder" 
                className="w-full h-auto rounded-3xl shadow-lg"
              />
              <p className="text-sm italic text-[#1A1A1A]/70 mt-4 text-center">Mauricio Cader, Founder</p>
            </div>
            <div className="lg:col-span-3 lg:pl-12">
              <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-widest mb-4">About</p>
              <h2 className="text-4xl font-bold text-[#1A1A1A] mb-6">Founder-led means accountability</h2>
              <p className="text-lg text-[#1A1A1A]/75 leading-relaxed mb-6">
                I'm Mauricio Cader, founder of Cader Talent, where I help US companies hire reliable, culturally aligned talent from El Salvador through a short, human-centered hiring process.
              </p>
              
              <blockquote className="border-l-4 border-[#C67B5C] pl-6 py-4 mb-6 bg-white rounded-r-xl">
                <p className="text-xl italic text-[#1A1A1A] leading-relaxed mb-3">
                  "Attitude and accountability come first — skills are important and validated, but mindset ultimately determines long-term performance."
                </p>
                <cite className="text-sm font-semibold text-[#C67B5C] not-italic">— Mauricio Cader</cite>
              </blockquote>
              
              <p className="text-lg text-[#1A1A1A]/75 leading-relaxed mb-6">
                After years of building and managing teams across sales, marketing, and operations, I've seen firsthand what separates hires who perform from those who just look good on paper. In today's world, AI can make every resume look perfect, but real performance still comes from real conversation, judgment, and ownership. That's why our process prioritizes live interviews and human assessment over endless forms or scripts.
              </p>
              <p className="text-lg text-[#1A1A1A]/75 leading-relaxed">
                Hiring shouldn't be slower or more complicated — it should be better. That's exactly what we build.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-36 bg-[#F5F5F3]">
        <div className="max-w-2xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-serif text-[#1A1A1A] mb-4">Let's Talk</h2>
            <p className="text-lg text-[#1A1A1A]/70 mb-6">Book a 15-minute conversation to discuss your hiring needs</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a href="tel:+18329243888" className="inline-flex items-center gap-3 bg-[#C67B5C] text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-[#B36A4D] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-line text-xl"></i>
                (832) 924-3888
              </a>
              <span className="text-[#1A1A1A]/40">or</span>
              <a href="mailto:mauricio@cadertalent.com" className="inline-flex items-center gap-3 bg-white text-[#1A1A1A] px-8 py-4 rounded-full text-lg font-medium hover:bg-[#1A1A1A]/5 transition-colors border-2 border-[#1A1A1A]/10 cursor-pointer whitespace-nowrap">
                <i className="ri-mail-line text-xl"></i>
                Email Us
              </a>
            </div>
            <p className="text-base text-[#1A1A1A]/60">Or fill out the form below</p>
          </div>
          
          <form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
                className="w-full px-4 py-4 bg-white border border-[#1A1A1A]/20 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
              />
            </div>
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                required
                className="w-full px-4 py-4 bg-white border border-[#1A1A1A]/20 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
              />
            </div>
            <div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone Number (Optional)"
                className="w-full px-4 py-4 bg-white border border-[#1A1A1A]/20 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
              />
            </div>
            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell me about your hiring needs"
                rows={4}
                maxLength={500}
                required
                className="w-full px-4 py-4 bg-white border border-[#1A1A1A]/20 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors resize-none"
              ></textarea>
              <p className="text-sm text-[#1A1A1A]/50 mt-2">{formData.message.length}/500 characters</p>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#C67B5C] text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-[#B36A4D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-center">
                Something went wrong. Please email directly at mauricio@cadertalent.com
              </div>
            )}
          </form>
          <p className="text-sm text-[#1A1A1A]/60 text-center mt-6">Messages sent to mauricio@cadertalent.com · Usually respond within 4 hours</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-16">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div className="lg:col-span-2">
              <div className="flex items-center mb-4">
                <img src="https://static.readdy.ai/image/5287d4f4544f3b52e73e5c6adbaab8fc/8a8de03178bcb555df0aaf16906b9e92.png" alt="Cader Talent" className="h-40 w-auto" />
              </div>
              <p className="text-white/60 text-base mb-8">Human judgment in modern hiring</p>
              <div className="space-y-3">
                <a href="tel:+18329243888" className="flex items-center gap-2 text-base text-white hover:text-[#C67B5C] transition-colors hover:underline cursor-pointer">
                  <i className="ri-phone-line"></i>
                  (832) 924-3888
                </a>
                <a href="mailto:mauricio@cadertalent.com" className="flex items-center gap-2 text-base text-white hover:text-[#C67B5C] transition-colors hover:underline cursor-pointer">
                  <i className="ri-mail-line"></i>
                  mauricio@cadertalent.com
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm text-white/60 font-semibold mb-4 uppercase tracking-wider">Quick Links</h4>
              <div className="space-y-3">
                <a href="#how-it-works" className="block text-base text-white hover:text-[#C67B5C] transition-colors cursor-pointer">How It Works</a>
                <Link to="/cost-calculator" className="block text-base text-white hover:text-[#C67B5C] transition-colors cursor-pointer">Cost Calculator</Link>
                <a href="#why-us" className="block text-base text-white hover:text-[#C67B5C] transition-colors cursor-pointer">For Candidates</a>
                <a href="#about" className="block text-base text-white hover:text-[#C67B5C] transition-colors cursor-pointer">About</a>
              </div>
            </div>
            <div>
              <h4 className="text-sm text-white/60 font-semibold mb-4 uppercase tracking-wider">Connect</h4>
              <a href="https://www.linkedin.com/company/cader-talent" target="_blank" rel="noopener noreferrer" className="block text-base text-white hover:text-[#C67B5C] transition-colors mb-3 hover:underline cursor-pointer">Company LinkedIn</a>
              <a href="https://www.linkedin.com/in/mauricio-cader-montealegre-88875b16/" target="_blank" rel="noopener noreferrer" className="block text-base text-white hover:text-[#C67B5C] transition-colors mb-3 hover:underline cursor-pointer">Mauricio's LinkedIn</a>
              <a href="https://www.facebook.com/profile.php?id=61586798219463" target="_blank" rel="noopener noreferrer" className="block text-base text-white hover:text-[#C67B5C] transition-colors mb-3 hover:underline cursor-pointer">Facebook Page</a>
              <p className="text-sm text-white/50">San Salvador, El Salvador</p>
            </div>
          </div>
          <div className="border-t border-white/15 pt-8 text-center">
            <p className="text-sm text-white/40">© 2025 Cader Talent. Built on trust. · <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#C67B5C] transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
